import type { Metadata } from "@/content/types";
import { URLS } from "@/lib/consts";

//
// Industries
//
const INDUSTRIES: Metadata[] = [
  {
    key: "manufacturing",
    title: "Manufacturing CPAs & Cost Accounting",
    description:
      "Job costing, inventory valuation, and margin control for manufacturers. Get reliable financials, proactive tax planning, and scalable reporting.",
    canonical: URLS.INDUSTRIES.MANUFACTURING,
    primaryImage: {
      src: "manufacturing",
      width: 1600,
      height: 900,
    },
  },
  {
    key: "construction",
    title: "Construction CPAs & Job Costing",
    description:
      "WIP schedules, progress billing, retainage, and cash flow for GCs and trades. Tighten controls and deliver lender-ready financials.",
    canonical: URLS.INDUSTRIES.CONSTRUCTION,
    primaryImage: {
      src: "construction",
      width: 1600,
      height: 900,
    },
  },
  {
    key: "real-estate",
    title: "Real Estate Accounting & 1031 Strategy",
    description:
      "Entity structure, depreciation, and investor reporting for rentals, syndications, and development. Coordinate cost seg and clean financials.",
    canonical: URLS.INDUSTRIES.REAL_ESTATE,
    primaryImage: {
      src: "real-estate",
      width: 1600,
      height: 900,
    },
  },
  {
    key: "technology",
    title: "Tech & SaaS Finance Operations",
    description:
      "ARR/MRR metrics, revenue recognition, close discipline, and R&D credits. Build scalable reporting and fundraising-ready packages.",
    canonical: URLS.INDUSTRIES.TECHNOLOGY,
    primaryImage: {
      src: "technology",
      width: 1600,
      height: 900,
    },
  },
  {
    key: "transportation",
    title: "Transportation Accounting & IFTA",
    description:
      "Fleet financials, IFTA reporting, equipment depreciation, and route/lane visibility. Improve cash forecasting and audit readiness.",
    canonical: URLS.INDUSTRIES.TRANSPORTATION,
    primaryImage: {
      src: "transportation",
      width: 1600,
      height: 900,
    },
  },
  {
    key: "oil-and-gas",
    title: "Oil & Gas Accounting and Depletion",
    description:
      "Lease accounting, JIB/royalties, and depletion for operators, services, and investors. Standardize reporting and strengthen compliance.",
    canonical: URLS.INDUSTRIES.OIL_AND_GAS,
    primaryImage: {
      src: "oil-and-gas",
      width: 1600,
      height: 900,
    },
  },
  {
    key: "healthcare",
    title: "Healthcare CPA Services",
    description:
      "Revenue cycle clarity, compliance, and audit-ready books for medical and dental practices. Align practice management with accounting.",
    canonical: URLS.INDUSTRIES.HEALTHCARE,
    primaryImage: {
      src: "healthcare",
      width: 1600,
      height: 900,
    },
  },
  {
    key: "ranching-and-agribusiness",
    title: "Ranching & Agribusiness Accounting",
    description:
      "Inventory, biological assets, and seasonal cash flow planning. Ag-specific deductions and schedules tailored for producers and ranchers.",
    canonical: URLS.INDUSTRIES.RANCHING_AND_AGRIBUSINESS,
    primaryImage: {
      src: "ranching-and-agribusiness",
      width: 1600,
      height: 900,
    },
  },
  {
    key: "professional",
    title: "Professional Services Firm Accounting",
    description:
      "Utilization, WIP, and pricing insights for law, architecture, consulting, and agencies. Improve billing, KPIs, and partner visibility.",
    canonical: URLS.INDUSTRIES.PROFESSIONAL,
    primaryImage: {
      src: "professional",
      width: 1600,
      height: 900,
    },
  },
  {
    key: "nonprofits",
    title: "Nonprofit Fund Accounting & Reporting",
    description:
      "Transparent fund accounting, grant tracking, and GAAP-compliant statements. Board-ready reporting and audit/review support.",
    canonical: URLS.INDUSTRIES.NONPROFITS,
    primaryImage: {
      src: "nonprofits",
      width: 1600,
      height: 900,
    },
  },
];

export const METADATA_INDUSTRY_BY_KEY = Object.entries(
  INDUSTRIES.map(metadata => [metadata.key, metadata]),
);

//
// Service Items
//
const SERVICE_ITEMS: Metadata[] = [
  {
    key: "accounting-system-selection-and-setup",
    title: "Accounting System Selection & Setup",
    description:
      "Choose, configure, and migrate to the right accounting stack—clean COA, smart integrations, and a scalable foundation for reliable reporting.",
    canonical: URLS.SERVICES.ACCOUNTING.ACCOUNTING_SYSTEM_SELECTION_AND_SETUP,
    pageType: "WebPage",
    breadcrumb: true,
    service: {
      serviceType: "Accounting System Selection & Setup",
      areaServed: ["Texas", "Houston"],
    },
    primaryImage: {
      src: "accounting-system-selection-and-setup",
      width: 1600,
      height: 900,
    },
  },
  {
    key: "compilation-of-financial-statements",
    title: "Compilation of Financial Statements",
    description:
      "GAAP-presented financial statement compilations from client data—clear, lender-friendly reporting for planning and credibility.",
    canonical: URLS.SERVICES.ACCOUNTING.COMPILATION_OF_FINANCIAL_STATEMENTS,
  },
  {
    key: "financial-position-job-descriptions",
    title: "Finance Team Role Definitions",
    description:
      "Role clarity for finance positions—responsibilities, controls, and reporting lines that reduce risk and support scalable operations.",
    canonical: URLS.SERVICES.ACCOUNTING.FINANCIAL_POSITION_JOB_DESCRIPTIONS,
  },
  {
    key: "policies-and-procedures",
    title: "Financial Policies & Procedures",
    description:
      "Practical, enforceable policies and procedures that tighten internal controls, standardize processes, and ensure compliance.",
    canonical: URLS.SERVICES.ACCOUNTING.POLICIES_AND_PROCEDURES,
  },
  {
    key: "preparation-of-financial-statements",
    title: "Preparation of Financial Statements",
    description:
      "Timely, organized financial statements for management, lenders, and auditors—built for accuracy, clarity, and confident decisions.",
    canonical: URLS.SERVICES.ACCOUNTING.PREPARATION_OF_FINANCIAL_STATEMENTS,
  },
  {
    key: "software-selection-and-implementation",
    title: "Accounting Software Implementation",
    description:
      "End-to-end selection and implementation—integrations, workflows, and COA design for efficient, trustworthy financial data.",
    canonical: URLS.SERVICES.ACCOUNTING.SOFTWARE_SELECTION_AND_IMPLEMENTATION,
  },
  {
    key: "quickbooks-services",
    title: "QuickBooks Setup, Training & Support",
    description:
      "QuickBooks setup, cleanup, training, and ongoing support for Houston businesses. We configure QuickBooks Online or Desktop for accurate books and confident reporting.",
    canonical: URLS.SERVICES.ACCOUNTING.QUICKBOOKS_SERVICES,
  },

  // Advisory
  {
    key: "budgeting-and-forecasts",
    title: "Budgeting & Financial Forecasting",
    description:
      "Realistic budgets and rolling forecasts aligned to strategy and cash—turn plans into measurable, actionable targets.",
    canonical: URLS.SERVICES.ADVISORY.BUDGETING_AND_FORECASTS,
  },
  {
    key: "cash-flow-management",
    title: "Cash Flow Management",
    description:
      "Model inflows/outflows, optimize working capital, and plan contingencies—keep operations predictable and ready for growth.",
    canonical: URLS.SERVICES.ADVISORY.CASH_FLOW_MANAGEMENT,
  },
  {
    key: "disclosures-and-budget-support",
    title: "Disclosures & Budget Support",
    description:
      "Accurate disclosures, supporting schedules, and grant budgets that satisfy reporting requirements and streamline audits.",
    canonical: URLS.SERVICES.ADVISORY.DISCLOSURES_AND_BUDGET_SUPPORT,
  },
  {
    key: "gaap-implementation",
    title: "GAAP Implementation",
    description:
      "Hands-on GAAP adoption—policies, chart structures, and recognition rules that elevate compliance and reporting quality.",
    canonical: URLS.SERVICES.ADVISORY.GAAP_IMPLEMENTATION,
  },
  {
    key: "medicare-cost-reports",
    title: "Medicare Cost Report Preparation",
    description:
      "Compliant Medicare cost reports for hospices and providers—defensible allocations and timely submission for reimbursement.",
    canonical: URLS.SERVICES.ADVISORY.MEDICARE_COST_REPORTS,
  },
  {
    key: "supplemental-schedules",
    title: "Supplemental Schedules",
    description:
      "Audit-ready schedules that reconcile to your books—support disclosures, grants, and management reporting with confidence.",
    canonical: URLS.SERVICES.ADVISORY.SUPPLEMENTAL_SCHEDULES,
  },
  {
    key: "succession-planning-and-wealth-transfer",
    title: "Succession Planning & Wealth Transfer",
    description:
      "Coordinate tax, estate, and business-succession strategies that protect family wealth and ensure continuity over generations.",
    canonical: URLS.SERVICES.ADVISORY.SUCCESSION_PLANNING_AND_WEALTH_TRANSFER,
  },

  // Audit
  {
    key: "audit-of-financial-statements",
    title: "Financial Statement Audits",
    description:
      "Independent audits that build trust, strengthen controls, and meet compliance—with clear timelines and practical requests.",
    canonical: URLS.SERVICES.AUDIT.AUDIT_OF_FINANCIAL_STATEMENTS,
  },
  {
    key: "audits-reviews-and-financial-statements",
    title: "Audits, Reviews & Compilations",
    description:
      "Right-sized assurance—audits, reviews, or compilations—to satisfy lenders, boards, and regulatory requirements.",
    canonical: URLS.SERVICES.AUDIT.AUDITS_REVIEWS_AND_FINANCIAL_STATEMENTS,
  },
  {
    key: "internal-control-documentation",
    title: "Internal Control Documentation",
    description:
      "Assess and document internal controls—reduce risk, improve reliability, and prepare for audits and future growth.",
    canonical: URLS.SERVICES.AUDIT.INTERNAL_CONTROL_DOCUMENTATION,
  },
  {
    key: "internal-controls",
    title: "Internal Controls Consulting",
    description:
      "Design practical control frameworks that safeguard assets, prevent errors, and support reliable financial reporting.",
    canonical: URLS.SERVICES.AUDIT.INTERNAL_CONTROLS,
  },
  {
    key: "review-of-financial-statements",
    title: "CPA Review of Financial Statements",
    description:
      "Limited assurance via analytics and inquiries—elevate credibility without the scope of a full audit.",
    canonical: URLS.SERVICES.AUDIT.REVIEW_OF_FINANCIAL_STATEMENTS,
  },
  {
    key: "special-purpose-financial-statements",
    title: "Special-Purpose Financial Statements",
    description:
      "Projections, cash-basis, tax-basis, and other frameworks tailored to specific stakeholder needs.",
    canonical: URLS.SERVICES.AUDIT.SPECIAL_PURPOSE_FINANCIAL_STATEMENTS,
  },

  // Business
  {
    key: "part-time-controller-cfo",
    title: "Fractional Controller & CFO",
    description:
      "Executive-level finance leadership—forecasting, KPIs, cash planning, and board-ready reporting without a full-time hire.",
    canonical: URLS.SERVICES.BUSINESS.PART_TIME_CONTROLLER_CFO,
  },
  {
    key: "debt-covenant-compliance",
    title: "Debt Covenant Compliance",
    description:
      "Timely covenant calculations, tracking, and lender-ready packages that reduce risk and preserve credit relationships.",
    canonical: URLS.SERVICES.BUSINESS.DEBT_COVENANT_COMPLIANCE,
  },
  {
    key: "development-of-budgets",
    title: "Development of Budgets",
    description:
      "Practical, measurable budgets that align resources with goals—creating visibility and accountability across teams.",
    canonical: URLS.SERVICES.BUSINESS.DEVELOPMENT_OF_BUDGETS,
  },
  {
    key: "development-of-financial-policies-and-procedures",
    title: "Financial Policy & Procedure Development",
    description:
      "Codify best practices with clear, implementable policies and procedures that standardize finance operations.",
    canonical:
      URLS.SERVICES.BUSINESS.DEVELOPMENT_OF_FINANCIAL_POLICIES_AND_PROCEDURES,
  },
  {
    key: "successful-planning",
    title: "Strategic Financial Planning",
    description:
      "Translate strategy into numbers—capital planning, scenarios, and milestones that keep your growth on track.",
    canonical: URLS.SERVICES.BUSINESS.SUCCESSFUL_PLANNING,
  },

  // Individual
  {
    key: "personal-financial-statements",
    title: "Personal Financial Statements",
    description:
      "Clear, lender-ready personal financial statements that present assets, liabilities, and net worth for planning and credit.",
    canonical: URLS.SERVICES.INDIVIDUAL.PERSONAL_FINANCIAL_STATEMENTS,
  },
  {
    key: "individual-accounting",
    title: "Individual Accounting Services",
    description:
      "Personalized accounting for complex finances—investments, K-1s, rentals, and planning that stays organized and accurate.",
    canonical: URLS.SERVICES.INDIVIDUAL.INDIVIDUAL_ACCOUNTING,
  },

  // Tax
  {
    key: "estate-and-trusts",
    title: "Estate, Trust & Gift Tax & Reporting",
    description:
      "Fiduciary returns (Form 1041), gift tax returns (Form 709), distributions, and wealth-transfer strategy—coordinated with attorneys and advisors to protect beneficiaries.",
    canonical: URLS.SERVICES.TAX.ESTATE_AND_TRUSTS,
  },
  {
    key: "federal-tax-return-preparation",
    title: "Federal Tax Return Preparation",
    description:
      "Accurate federal tax preparation for individuals, corporations, and partnerships—with opportunities to reduce liability.",
    canonical: URLS.SERVICES.TAX.FEDERAL_TAX_RETURN_PREPARATION,
  },
  {
    key: "state-franchise-income-and-tax-return",
    title: "State Franchise & Income Tax Returns",
    description:
      "Multistate compliance—nexus evaluation, apportionment, and filings that keep you compliant across jurisdictions.",
    canonical: URLS.SERVICES.TAX.STATE_FRANCHISE_INCOME_AND_TAX_RETURN,
  },
  {
    key: "tax-compliance",
    title: "Tax Compliance",
    description:
      "Year-round federal and state compliance—accurate filings, timely extensions, and proactive support that reduces surprises.",
    canonical: URLS.SERVICES.TAX.TAX_COMPLIANCE,
  },
  {
    key: "tax-planning",
    title: "Strategic Tax Planning",
    description:
      "Proactive, data-driven strategies—entity choice, timing, deductions, credits, and wealth transfer aligned to your goals.",
    canonical: URLS.SERVICES.TAX.TAX_PLANNING,
  },
  {
    key: "oil-and-gas-royalty-tax",
    title: "Oil & Gas Royalty & Mineral Owner Tax",
    description:
      "Tax help for Houston-area royalty and mineral owners: depletion, intangible drilling costs, working vs. royalty interest, and the K-1s and 1099-MISC behind your division orders.",
    canonical: URLS.SERVICES.TAX.OIL_AND_GAS_ROYALTY_TAX,
    service: {
      serviceType: "Oil & Gas Royalty Owner Tax",
      description:
        "Depletion, IDCs, working vs. royalty interest, lease bonus, and K-1/1099 reporting for mineral and royalty owners.",
      areaServed: ["Houston", "Texas"],
    },
  },
  {
    key: "ranch-and-landowner-tax",
    title: "Ranch, Farm & Landowner Tax in Texas",
    description:
      "Tax guidance for Texas ranchers, farmers, and landowners: ag and wildlife valuation, rollback exposure, mineral and royalty income, conservation easements, and land 1031 exchanges.",
    canonical: URLS.SERVICES.TAX.RANCH_AND_LANDOWNER_TAX,
    service: {
      serviceType: "Ranch, Farm & Landowner Tax",
      description:
        "Ag/wildlife valuation, rollback tax, mineral income, conservation easements, land 1031s, and multi-generational transfer.",
      areaServed: ["Houston", "Texas"],
    },
  },
  {
    key: "real-estate-investor-tax",
    title: "Real Estate Investor Tax & 1031 Strategy",
    description:
      "Tax strategy for Houston real estate investors: 1031 exchanges, cost segregation and bonus depreciation, real estate professional status, and passive-activity loss planning.",
    canonical: URLS.SERVICES.TAX.REAL_ESTATE_INVESTOR_TAX,
    service: {
      serviceType: "Real Estate Investor Tax",
      description:
        "1031 exchanges, cost segregation, bonus depreciation, REP status, and passive-activity rules for rentals and syndications.",
      areaServed: ["Houston", "Texas"],
    },
  },
  {
    key: "business-sale-exit",
    title: "Business Sale & Exit Tax Planning",
    description:
      "Plan the tax side of selling your business: QSBS, installment sales, asset vs. stock structuring, and purchase-price allocation—coordinated well before the letter of intent.",
    canonical: URLS.SERVICES.TAX.BUSINESS_SALE_EXIT,
    service: {
      serviceType: "Business Sale & Exit Planning",
      description:
        "Deal structuring, QSBS §1202, installment sales §453, purchase-price allocation, and pre-sale tax planning.",
      areaServed: ["Houston", "Texas"],
    },
  },
];

export const METADATA_SERVICE_BY_KEY = Object.fromEntries(
  SERVICE_ITEMS.map(metadata => [metadata.key, metadata]),
);

//
// Service Categories
//
const SERVICE_CATEGORIES: Metadata[] = [
  {
    key: "accounting",
    title: "Accounting Services & Financial Reporting",
    description:
      "Clean books, month-end close, and GAAP-ready financial statements. Configure systems, standardize COA, and deliver reliable reporting for Houston-area organizations.",
    canonical: URLS.SERVICES.ACCOUNTING.ROOT,
    pageType: "CollectionPage",
    primaryImage: {
      src: "accounting",
      width: 1600,
      height: 900,
    },
    webpage: {
      itemList: [
        "accounting-system-selection-and-setup",
        "compilation-of-financial-statements",
        "financial-position-job-descriptions",
        "policies-and-procedures",
        "preparation-of-financial-statements",
        "software-selection-and-implementation",
      ],
    },
  },
  {
    key: "advisory",
    title: "Advisory Services & Fractional CFO",
    description:
      "Budgets, forecasts, KPI dashboards, and strategic planning. Get executive-level clarity and data-driven decisions without a full-time hire.",
    canonical: URLS.SERVICES.ADVISORY.ROOT,
    pageType: "CollectionPage",
    primaryImage: {
      src: "advisory",
      width: 1600,
      height: 900,
    },
    webpage: {
      itemList: [
        "budgeting-and-forecasts",
        "cash-flow-management",
        "disclosures-and-budget-support",
        "gaap-implementation",
        "medicare-cost-reports",
        "succession-planning-and-wealth-transfer",
        "supplemental-schedules",
      ],
    },
  },
  {
    key: "audit",
    title: "Audit, Review & Compilation Services",
    description:
      "Right-sized assurance for lenders, boards, and grants. Audits, reviews, and compilations delivered with clear timelines and practical requests.",
    canonical: URLS.SERVICES.AUDIT.ROOT,
    pageType: "CollectionPage",
    primaryImage: {
      src: "audit",
      width: 1600,
      height: 900,
    },
    webpage: {
      itemList: [
        "audit-of-financial-statements",
        "audits-reviews-and-financial-statements",
        "internal-control-documentation",
        "internal-controls",
        "review-of-financial-statements",
        "special-purpose-financial-statements",
      ],
    },
  },
  {
    key: "business",
    title: "Business Services & Operations Support",
    description:
      "Entity setup, debt covenant compliance, policies & procedures, and part-time controller/CFO. Strengthen controls and improve visibility as you grow.",
    canonical: URLS.SERVICES.BUSINESS.ROOT,
    pageType: "CollectionPage",
    primaryImage: {
      src: "business",
      width: 1600,
      height: 900,
    },
    webpage: {
      itemList: [
        "development-of-financial-policies-and-procedures",
        "debt-covenant-compliance",
        "development-of-budgets",
        "successful-planning",
        "part-time-controller-cfo",
      ],
    },
  },
  {
    key: "individual",
    title: "Individual Tax & Accounting",
    description:
      "Personal accounting, rental schedules, and lender-ready personal financial statementsΓÇöorganized, accurate, and ready for timely filings.",
    canonical: URLS.SERVICES.INDIVIDUAL.ROOT,
    pageType: "CollectionPage",
    primaryImage: {
      src: "individual",
      width: 1600,
      height: 900,
    },
    webpage: {
      itemList: ["individual-accounting", "personal-financial-statements"],
    },
  },
  {
    key: "tax",
    title: "Tax Planning & Compliance",
    description:
      "Proactive planning and on-time filings across federal, Texas, and multi-state returns. Reduce surprises and keep compliance stress-free.",
    canonical: URLS.SERVICES.TAX.ROOT,
    pageType: "CollectionPage",
    primaryImage: {
      src: "tax",
      width: 1600,
      height: 900,
    },
    webpage: {
      itemList: [
        "estate-and-trusts",
        "federal-tax-return-preparation",
        "tax-planning",
        "state-franchise-income-and-tax-return",
        "tax-compliance",
      ],
    },
  },
];

export const METADATA_SERVICE_CATEGORY_BY_KEY = Object.entries(
  SERVICE_CATEGORIES.map(metadata => [metadata.key, metadata]),
);

const LOCATIONS: Metadata[] = [
  {
    key: "locations",
    title: "CPA Services in Houston, Texas & the Gulf Coast",
    description:
      "Explore Orsak CPA's service areas across Houston, Texas, and the Gulf Coast. Trusted accounting, tax, and advisory expertise for businesses and individuals.",
    canonical: URLS.LOCATIONS.ROOT,
  },
];

export const METADATA: Metadata[] = [
  ...INDUSTRIES,
  ...SERVICE_CATEGORIES,
  ...SERVICE_ITEMS,
  ...LOCATIONS,
  {
    key: "",
    title: "Houston CPA Firm for Tax, Accounting, & Advisory",
    description:
      "Orsak, Langner, & Barthel, PLLC is a trusted CPA firm in Houston, Texas. We provide tax, accounting, audit, and advisory services tailored to individuals, businesses, and nonprofits across Texas.",
    canonical: URLS.HOME,
    pageType: "WebPage",
    breadcrumb: false,
    primaryImage: {
      src: "downtown-houston-texas",
      width: 1600,
      height: 900,
    },
  },
  {
    key: "services",
    title: "Our CPA Services",
    description:
      "Explore the full range of CPA services—audits, financial statements, tax planning, compliance, and advisory—delivered by a trusted team in Houston, Texas.",
    canonical: URLS.SERVICES.ROOT,
    pageType: "CollectionPage",
    breadcrumb: true,
    primaryImage: {
      src: "services",
      width: 1600,
      height: 900,
    },
    itemList: {
      name: "Services",
      items: [
        "accounting",
        "advisory",
        "audit",
        "business",
        "individual",
        "tax",
      ],
    },
  },
  {
    key: "industries",
    title: "Industries We Serve",
    description:
      "Specialized CPA support for construction, healthcare, manufacturing, oil & gas, nonprofits, and more—serving Houston and clients across Texas.",
    canonical: URLS.INDUSTRIES.ROOT,
    pageType: "CollectionPage",
    breadcrumb: true,
    primaryImage: {
      src: "industries",
      width: 1600,
      height: 900,
    },
    itemList: {
      name: "Industries",
      items: [
        "construction",
        "manufacturing",
        "real-estate",
        "technology",
        "transportation",
        "oil-and-gas",
        "healthcare",
        "ranching-and-agribusiness",
        "professional",
        "nonprofits",
      ],
    },
  },
  {
    key: "contact-us",
    title: "Contact Us",
    description:
      "Have a question or need a CPA? Contact Orsak CPAs in Houston, Texas—tax, accounting, audit, and advisory. Request a consultation, ask about services, or get help with filings.",
    canonical: URLS.CONTACT_US,
    pageType: "ContactPage",
    breadcrumb: true,
    primaryImage: {
      src: "contact-orsak-cpas",
      width: 1600,
      height: 900,
    },
  },
  {
    key: "about",
    title: "About Our Firm",
    description:
      "Orsak Langner & Barthel PLLC is a Houston, Texas CPA firm serving businesses and individuals with accounting, tax, audit, and advisory services built on integrity and long-term relationships.",
    canonical: URLS.ABOUT,
    pageType: "AboutPage",
    breadcrumb: true,
    primaryImage: {
      src: "downtown-houston-texas",
      width: 1600,
      height: 900,
    },
  },
  {
    key: "privacy",
    title: "Privacy Policy",
    description:
      "How Orsak Langner & Barthel PLLC collects, uses, and protects the information you share with our Houston, Texas CPA firm.",
    canonical: URLS.PRIVACY,
    pageType: "WebPage",
    breadcrumb: true,
  },
  {
    key: "irs-resolution",
    title: "IRS Tax Resolution & Representation",
    description:
      "Resolve back taxes, liens, levies, wage garnishment, and unfiled returns. Orsak CPAs represents Houston, Texas taxpayers before the IRS and negotiates payment plans and offers in compromise.",
    canonical: URLS.SERVICES.TAX.IRS_RESOLUTION,
  },
];

export const METADATA_BY_KEY = Object.fromEntries(
  METADATA.map(metadata => [metadata.key, metadata]),
);
